import { ensureViewingAccess } from './wallet.js';

// ─────────────────────────────────────────────────────────────────────────
// Shared gate for the three places gated content is shown: a learner's own
// topic detail view (views/course.js renderDetail), a purchase filed into
// a course (views/course.js renderPurchaseDetail), and the marketplace "My
// purchases" viewer (views/marketplace.js openPurchaseViewer). All three
// call checkViewingAccess() before fetching/rendering the actual HTML, and
// render paywallMarkup() in its place if blocked. See functions/wallet.js
// ensureViewingAccess for the server-side billing logic — this module is
// deliberately thin, no business logic duplicated here.
// ─────────────────────────────────────────────────────────────────────────

/**
 * checkViewingAccess — resolves to { blocked, costPaise, ... }. Fails open
 * (blocked: false) on a network/Functions error rather than locking a
 * signed-in learner out of their own content over a transient hiccup —
 * the next check (their next click) will simply try again.
 */
export async function checkViewingAccess(opts) {
  try {
    return await ensureViewingAccess(opts);
  } catch (err) {
    console.error('ensureViewingAccess failed', err);
    return { blocked: false };
  }
}

/**
 * checkViewingAccessUnlessFree — free (₹0) marketplace items are exempt from
 * the monthly viewing subscription: they're always viewable, with no wallet
 * balance required. A caller that already knows an item is free passes
 * isFree=true and skips the subscription gate entirely; otherwise this is
 * just checkViewingAccess(). Keeps the "free means free" rule in one place
 * instead of duplicating the price check at every viewer.
 */
export async function checkViewingAccessUnlessFree(isFree, opts) {
  if (isFree) return { blocked: false, free: true };
  return checkViewingAccess(opts);
}

/**
 * paywallMarkup — dropped into a preview pane in place of the iframe when
 * checkViewingAccess() reports blocked. Points at the Wallet page rather
 * than embedding its own recharge flow, so there's exactly one recharge UI
 * in the app to keep in sync.
 */
export function paywallMarkup(access = {}) {
  const rupees = ((access.costPaise || 0) / 100).toFixed(0);

  // School licence has lapsed and this student can keep going on their own
  // wallet without unlinking — show a simple message + a one-click choice.
  // The button carries class js-self-fund-btn; renderViewingGate() wires it.
  if (access.schoolLapsed && access.canSelfFund) {
    const school = access.schoolName ? escapeHtml(access.schoolName) : 'Your school';
    return `
      <div class="empty-state viewing-paywall">
        <div style="font-size:2.5rem;">🏫</div>
        <h3>Your school’s subscription has ended</h3>
        <p>${school} is no longer covering your access. You can keep going on your own wallet — viewing is ₹${rupees}/month — without removing your school code. If your school renews, you’re covered again automatically.</p>
        <button class="btn btn-primary js-self-fund-btn">Continue with my own wallet</button>
      </div>
    `;
  }

  return `
    <div class="empty-state viewing-paywall">
      <div style="font-size:2.5rem;">🔒</div>
      <h3>Renew to keep viewing</h3>
      <p>Viewing your own content and paid purchases is ₹${rupees}/month, auto-charged to your wallet. Free (₹0) listings always stay free to view. Your balance is too low to renew — recharge to continue.</p>
      <a href="#/wallet" class="btn btn-primary">Recharge wallet</a>
    </div>
  `;
}

/**
 * renderViewingGate — drop the right paywall/message into `container` and, if
 * it's the "school ended, use your own wallet" case, wire the opt-in button:
 * clicking it records the self-fund choice (persisted server-side) and then
 * calls onRetry() to re-check access and render the content. For every other
 * blocked state it's just paywallMarkup() with its Recharge link. Keeps all
 * three viewers (own topic, filed purchase, marketplace purchase) identical.
 */
export function renderViewingGate(container, access, onRetry) {
  if (!container) return;
  container.innerHTML = paywallMarkup(access);
  const btn = container.querySelector('.js-self-fund-btn');
  if (btn && typeof onRetry === 'function') {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = 'Switching to your wallet…';
      try {
        await checkViewingAccess({ optInSelfFund: true });
      } catch (e) { /* onRetry re-checks and will surface any issue */ }
      onRetry();
    });
  }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
